<template>
  <div ref="raiz" class="drf">
    <button
      ref="disparador"
      type="button"
      class="drf-btn"
      :class="{ seleccionado: desde || hasta }"
      :aria-label="ariaLabel"
      :aria-expanded="abierto ? 'true' : 'false'"
      aria-haspopup="dialog"
      :data-testid="testid"
      @click="alternar"
    >
      <AppIcon name="calendar" size="h-4 w-4" />
      <span class="drf-copy">
        <span class="drf-label">{{ label }}</span>
        <span class="drf-valor">{{ texto }}</span>
      </span>
      <AppIcon name="chevron-down" size="h-3.5 w-3.5" class="drf-chevron" :class="{ girado: abierto }" />
    </button>

    <div v-if="abierto" class="drf-fondo" aria-hidden="true" @click="cerrar"></div>
    <transition name="drf">
      <div
        v-if="abierto"
        class="drf-panel"
        :class="{ 'drf-derecha': alinearDerecha }"
        role="dialog"
        :aria-label="'Filtrar por fecha de ' + label.toLowerCase()"
        :data-testid="testid + '-panel'"
      >
        <div class="drf-rapidos" role="group" aria-label="Rangos rápidos">
          <template v-for="(opcion, i) in opciones">
            <p
              v-if="opcion.grupo && opcion.grupo !== (opciones[i - 1] || {}).grupo"
              :key="'grupo-' + opcion.clave"
              class="drf-grupo"
            >
              {{ opcion.grupo }}
            </p>
            <button
            :key="opcion.clave"
            type="button"
            class="drf-opcion"
            :class="{ activa: seleccion === opcion.clave }"
            :aria-pressed="seleccion === opcion.clave ? 'true' : 'false'"
            :data-testid="testid + '-' + opcion.clave"
            @click="elegirRapido(opcion.clave)"
          >
            <span>{{ opcion.label }}</span>
            <AppIcon v-if="seleccion === opcion.clave" name="check" size="h-4 w-4" />
            </button>
          </template>
          <span class="drf-separador" aria-hidden="true"></span>
          <button
            type="button"
            class="drf-opcion"
            :class="{ activa: seleccion === 'todas' }"
            :aria-pressed="seleccion === 'todas' ? 'true' : 'false'"
            :data-testid="testid + '-todas'"
            @click="emitir('', '')"
          >
            <span>Cualquier fecha</span>
            <AppIcon v-if="seleccion === 'todas'" name="check" size="h-4 w-4" />
          </button>
        </div>
        <div class="drf-personalizado">
          <p class="drf-subtitulo" :class="{ activa: seleccion === 'personalizado' }">Personalizado</p>
          <RangeCalendar
            inline
            :inicio="borradorDesde"
            :fin="borradorHasta"
            @pick="elegirDia"
            @apply="emitir(borradorDesde, borradorHasta)"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
/*
 * DateRangeFilter — filtro de rango de fechas en un solo control.
 *
 * Botón compacto que abre un panel con rangos rápidos y un calendario para
 * un rango personalizado. Cada pantalla pasa sus rangos en `opciones`
 * (por defecto: este mes, este año, último año, últimos 2 años); un rango
 * puede ser abierto ("Hace más de 6 meses" = sin fecha de inicio).
 * Un rango rápido se aplica con un clic; el personalizado con "Aplicar".
 * Se cierra con clic fuera, Esc o al mover el foco fuera del panel.
 * En móvil el panel sale como hoja inferior.
 *
 * Uso: <DateRangeFilter label="Alta" :desde="d" :hasta="h" @change="({ desde, hasta }) => …" />
 */
import AppIcon from '@/components/ui/AppIcon.vue'
import RangeCalendar from '@/components/dashboard/RangeCalendar.vue'
import { RANGOS_RAPIDOS, etiquetaRango, rangoRapido, rangoRapidoActivo } from '@/dashboard/calendar'

const ANCHO_PANEL = 536

export default {
  name: 'DateRangeFilter',
  components: { AppIcon, RangeCalendar },
  props: {
    desde: { type: String, default: '' },
    hasta: { type: String, default: '' },
    label: { type: String, default: 'Fecha' },
    testid: { type: String, default: 'filtro-fechas' },
    // [{ clave, label, grupo? }] con claves que entiende rangoRapido
    opciones: { type: Array, default: () => RANGOS_RAPIDOS },
  },
  data() {
    return {
      abierto: false,
      alinearDerecha: false,
      borradorDesde: '',
      borradorHasta: '',
    }
  },
  computed: {
    seleccion() {
      if (!this.desde && !this.hasta) return 'todas'
      return rangoRapidoActivo(this.desde, this.hasta, new Date(), this.opciones) || 'personalizado'
    },
    texto() {
      if (this.seleccion === 'todas') return 'Cualquier fecha'
      const rapido = this.opciones.find((o) => o.clave === this.seleccion)
      if (rapido) return rapido.label
      return etiquetaRango(this.desde, this.hasta)
    },
    ariaLabel() {
      return this.seleccion === 'todas'
        ? `Filtrar por fecha de ${this.label.toLowerCase()}`
        : `Fecha de ${this.label.toLowerCase()}: ${this.texto}`
    },
  },
  watch: {
    abierto(valor) {
      const accion = valor ? 'addEventListener' : 'removeEventListener'
      document[accion]('mousedown', this.alInteractuarFuera)
      document[accion]('focusin', this.alInteractuarFuera)
      document[accion]('keydown', this.alTeclear)
    },
  },
  beforeDestroy() {
    this.abierto = false
    document.removeEventListener('mousedown', this.alInteractuarFuera)
    document.removeEventListener('focusin', this.alInteractuarFuera)
    document.removeEventListener('keydown', this.alTeclear)
  },
  methods: {
    alternar() {
      if (this.abierto) return this.cerrar()
      this.borradorDesde = this.desde
      this.borradorHasta = this.hasta
      // Si el panel no cabe a la derecha del botón, se alinea a su borde derecho
      const rect = this.$refs.disparador.getBoundingClientRect()
      this.alinearDerecha = rect.left + ANCHO_PANEL > window.innerWidth - 16
      this.abierto = true
    },
    cerrar() {
      this.abierto = false
    },
    alInteractuarFuera(event) {
      if (this.$refs.raiz && !this.$refs.raiz.contains(event.target)) this.cerrar()
    },
    alTeclear(event) {
      if (event.key !== 'Escape') return
      this.cerrar()
      this.$refs.disparador?.focus()
    },
    elegirRapido(clave) {
      const rango = rangoRapido(clave)
      this.emitir(rango.inicio, rango.fin)
    },
    elegirDia(rango) {
      this.borradorDesde = rango.inicio
      this.borradorHasta = rango.fin
    },
    emitir(desde, hasta) {
      this.$emit('change', { desde, hasta })
      this.cerrar()
    },
  },
}
</script>

<style scoped>
.drf {
  position: relative;
  display: flex;
  min-width: 0;
  align-items: center;
}
.drf-btn {
  display: inline-flex;
  max-width: 100%;
  min-height: 42px;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  border: 1px solid #d8e0ea;
  border-radius: 11px;
  background: #fff;
  color: #0b1b2e;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.16s ease, background 0.16s ease;
}
.drf-btn:hover,
.drf-btn.seleccionado {
  border-color: #9bb6d4;
  background: #f8fafc;
}
.drf-btn:focus-visible,
.drf-opcion:focus-visible {
  outline: 3px solid #7ab9f2;
  outline-offset: 2px;
}
.drf-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.1;
}
.drf-label {
  color: #607187;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.drf-valor {
  max-width: 190px;
  overflow: hidden;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.drf-chevron {
  flex: none;
  color: #607187;
  transition: transform 0.16s ease;
}
.drf-chevron.girado {
  transform: rotate(180deg);
}
.drf-fondo {
  display: none;
}
.drf-panel {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 60;
  display: flex;
  gap: 14px;
  padding: 14px;
  border: 1px solid #e4e9ef;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 12px 32px rgba(16, 42, 67, 0.16);
  font-family: Inter, Roboto, sans-serif;
}
.drf-panel.drf-derecha {
  right: 0;
  left: auto;
}
.drf-rapidos {
  display: flex;
  width: 204px;
  flex: none;
  flex-direction: column;
  gap: 2px;
  padding-right: 14px;
  border-right: 1px solid #eef2f6;
}
.drf-opcion {
  display: flex;
  min-height: 38px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 10px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #3d4b5f;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition: background 0.16s ease, color 0.16s ease;
}
.drf-opcion:hover:not(.activa) {
  background: #edf3f8;
  color: #0b1b2e;
}
.drf-opcion span {
  white-space: nowrap;
}
.drf-opcion.activa {
  background: #12304f;
  color: #fff;
}
.drf-grupo {
  margin: 6px 10px 2px;
  color: #607187;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.drf-grupo:first-child {
  margin-top: 0;
}
.drf-separador {
  height: 1px;
  margin: 6px 4px;
  background: #eef2f6;
}
.drf-personalizado {
  width: 280px;
  flex: none;
}
.drf-subtitulo {
  margin: 0 0 6px;
  color: #607187;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.drf-subtitulo.activa {
  color: #0a4f9c;
}
.drf-enter-active,
.drf-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.drf-enter,
.drf-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
@media (prefers-reduced-motion: reduce) {
  .drf-enter-active,
  .drf-leave-active,
  .drf-chevron {
    transition: none;
  }
}
@media (max-width: 700px) {
  .drf-fondo {
    position: fixed;
    inset: 0;
    z-index: 59;
    display: block;
    background: rgba(11, 27, 46, 0.35);
  }
  .drf-panel,
  .drf-panel.drf-derecha {
    position: fixed;
    top: auto;
    right: 0;
    bottom: 0;
    left: 0;
    max-height: 85dvh;
    flex-direction: column;
    overflow-y: auto;
    border-radius: 18px 18px 0 0;
  }
  .drf-rapidos {
    width: auto;
    flex-direction: row;
    flex-wrap: wrap;
    padding: 0 0 12px;
    border-right: 0;
    border-bottom: 1px solid #eef2f6;
  }
  .drf-opcion {
    border: 1px solid #e2e8ef;
  }
  .drf-separador {
    display: none;
  }
  .drf-personalizado {
    width: 100%;
  }
  .drf-personalizado ::v-deep .cal-panel {
    width: 100%;
  }
}
</style>
